import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { API_BASE_URL, API_ENDPOINTS, DEFAULT_HEADERS, getAuthHeaders } from '../config/api.ts';
import { useAuth } from './AuthContext.tsx';

export interface Kopek {
  _id: string;
  name: string;
  breed: string;
  gender: string;
  birthDate: string;
  weight?: number;
  size: string;
  color: string;
  profileImage?: string;
  description?: string;
  owner: {
    _id: string;
    name: string;
    surname: string;
    email: string;
    phone: string;
  };
  isActive: boolean;
  isPublic: boolean;
  createdAt: string;
  updatedAt: string;
  checkinInfo?: {
    id: string;
    checkInTime: string;
    lastUpdateTime?: string;
    branch: {
      _id: string;
      name: string;
      code: string;
    };
    staff: {
      _id: string;
      name: string;
      email: string;
      phone: string;
      position: string;
      branch: string;
    };
  };
}



export interface KopekGirisCikis {
  id: number;
  kopekId: number; // Kayıtlı köpeğin ID'si
  kopekAdi: string;
  cinsi: string;
  sahibiAdi: string;
  telefon: string;
  girisSaati: string;
  cikisSaati: string | null;
  tarih: string;
  konaklamaTuru?: string; // API'den gelecek
}

interface KopekContextType {
  kopekler: Kopek[];
  girisCikisVerileri: KopekGirisCikis[];
  loading: boolean;
  error: string | null;
  fetchKopekler: () => Promise<void>;
  getToplamKopekSayisi: () => number;
  getOteldeKiKopekSayisi: () => number;
  getBuHaftaGelenKopekSayisi: () => number;
  getFarkliCinsSayisi: () => number;
  getBugunGirisCikisVerileri: () => KopekGirisCikis[];
  getOteldeKalanKopekler: (tarih?: string) => KopekGirisCikis[];
  getKopeklerWithSonGiris: () => Kopek[];
  getKopekSonGirisTarihi: (kopekId: number) => string | null;
}

const KopekContext = createContext<KopekContextType | undefined>(undefined);

export const useKopek = () => {
  const context = useContext(KopekContext);
  if (context === undefined) {
    throw new Error('useKopek must be used within a KopekProvider');
  }
  return context;
};

interface KopekProviderProps {
  children: ReactNode;
}

export const KopekProvider: React.FC<KopekProviderProps> = ({ children }) => {
  const [kopekler, setKopekler] = useState<Kopek[]>([]);
  const [girisCikisVerileri, setGirisCikisVerileri] = useState<KopekGirisCikis[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  const { token, isAuthenticated, isInitialized } = useAuth();

  // Aktif check-in'i olan köpekleri API'den çek
  const fetchKopekler = async () => {
    try {
      setLoading(true);
      setError(null);
      
      if (!isAuthenticated || !token) {
        throw new Error('Admin token bulunamadı');
      }
      
      const headers = getAuthHeaders(token);
      
      const response = await fetch(`${API_BASE_URL}${API_ENDPOINTS.ACTIVE_DOGS}`, {
        method: 'GET',
        headers,
      });

      const data = await response.json();

      if (data.success) {
        
        // API'den gelen veriyi Kopek tipine dönüştür
        const activeDogs: Kopek[] = data.data.map((checkin: any) => {
          
          return {
            _id: checkin.id,
            name: checkin.name,
            breed: checkin.breed,
            gender: '', // Şimdilik boş bırakalım
            birthDate: '', // Şimdilik boş bırakalım
            size: '', // Şimdilik boş bırakalım
            color: '', // Şimdilik boş bırakalım
            profileImage: checkin.profileImage,
            description: '', // Şimdilik boş bırakalım
            owner: {
              _id: '', // Şimdilik boş bırakalım
              name: checkin.ownerName,
              email: checkin.ownerEmail,
              phone: checkin.ownerPhone,
            },
            isActive: true,
            isPublic: false,
            createdAt: checkin.checkInTime,
            updatedAt: checkin.lastUpdateTime || checkin.checkInTime,
            // Ek check-in bilgileri
            checkinInfo: {
              id: checkin.checkinId,
              checkInTime: checkin.checkInTime,
              lastUpdateTime: checkin.lastUpdateTime,
              branch: {
                _id: '', // Şimdilik boş bırakalım
                name: checkin.branchName,
                code: '', // Şimdilik boş bırakalım
              },
              staff: {
                _id: '', // Şimdilik boş bırakalım
                name: checkin.staffName,
                email: checkin.staffEmail,
                phone: checkin.staffPhone,
                position: checkin.staffPosition,
                branch: '', // Şimdilik boş bırakalım
              }
            }
          };
        });
        
        setKopekler(activeDogs);
      } else {
        throw new Error(data.message || 'Kedi/Köpek listesi getirilemedi');
      }
    } catch (error) {
      setError(error instanceof Error ? error.message : 'Bilinmeyen hata');
    } finally {
      setLoading(false);
    }
  };



  // Toplam köpek sayısı
  const getToplamKopekSayisi = () => {
    return kopekler.length;
  };



  // Oteldeki köpek sayısı (mock data - gerçek API'den gelecek)
  const getOteldeKiKopekSayisi = () => {
    // Bu veri checkin API'sinden gelecek
    return Math.floor(Math.random() * 10) + 5; // Mock data
  };

  // Bu hafta gelen köpek sayısı
  const getBuHaftaGelenKopekSayisi = () => {
    const oneWeekAgo = new Date();
    oneWeekAgo.setDate(oneWeekAgo.getDate() - 7);
    
    return kopekler.filter(kopek => {
      const createdAt = new Date(kopek.createdAt);
      return createdAt >= oneWeekAgo;
    }).length;
  };

  // Farklı cins sayısı
  const getFarkliCinsSayisi = () => {
    const uniqueBreeds = new Set(kopekler.map(kopek => kopek.breed));
    return uniqueBreeds.size;
  };

  // Bugün giriş çıkış verileri (mock data)
  const getBugunGirisCikisVerileri = () => {
    return girisCikisVerileri.filter(kayit => {
      const kayitTarihi = new Date(kayit.tarih);
      const bugun = new Date();
      return kayitTarihi.toDateString() === bugun.toDateString();
    });
  };

  // Otelde kalan köpekler (mock data)
  const getOteldeKalanKopekler = (tarih?: string) => {
    return girisCikisVerileri.filter(kayit => 
      kayit.cikisSaati === null
    );
  };

  // Son giriş tarihi olan köpekler
  const getKopeklerWithSonGiris = () => {
    return kopekler; // API'den gelen veri zaten güncel
  };

  // Köpek son giriş tarihi
  const getKopekSonGirisTarihi = (kopekId: number) => {
    const kayit = girisCikisVerileri.find(k => k.kopekId === kopekId);
    return kayit ? kayit.girisSaati : null;
  };

  // Component mount olduğunda ve authentication durumu değiştiğinde verileri çek
  useEffect(() => {
    if (isInitialized && isAuthenticated && token) {
      fetchKopekler();
    }
  }, [isInitialized, isAuthenticated, token]);

  const value: KopekContextType = {
    kopekler,
    girisCikisVerileri,
    loading,
    error,
    fetchKopekler,
    getToplamKopekSayisi,
    getOteldeKiKopekSayisi,
    getBuHaftaGelenKopekSayisi,
    getFarkliCinsSayisi,
    getBugunGirisCikisVerileri,
    getOteldeKalanKopekler,
    getKopeklerWithSonGiris,
    getKopekSonGirisTarihi,
  };

  return (
    <KopekContext.Provider value={value}>
      {children}
    </KopekContext.Provider>
  );
};